import { ReactNode } from 'react';

import { ApiOrgType } from 'api/org/types/ApiOrgType';
import { ApiActionType } from 'api/volops';
import { ActivelyHiringBadge } from 'components/ActivelyHiringBadge/ActivelyHiringBadge';
import { Box } from 'components/Box';
import { Hide } from 'components/Hide';
import { RouterLink } from 'components/Link';
import { ListingTypeLabel } from 'components/Listing/ListingTypeLabel';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { SearchContextHeader } from 'components/Search/SearchContextHeader';
import { H1 } from 'components/Text/H1';
import { SearchContextHeaderData } from 'containers/SearchContextHeader/SearchContextHeaderData';
import { ListingLayout } from 'layouts/Listing/ListingLayout';

type Props = {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'ORG';
  actionType?: ApiActionType | null | undefined;
  orgType?: ApiOrgType;
  isHiring?: boolean;
  title: ReactNode;
  subtitle: ReactNode;
  details: ReactNode;
  sidebarButtons?: ReactNode;
  sidebarTitle?: (string | null | undefined) | (ReactNode | null | undefined);
  sidebarImage?: (string | null | undefined) | (ReactNode | null | undefined);
  sidebarHeader?: ReactNode;
  sidebarDetails?: ReactNode;
  sidebarLinks?: ReactNode | null | undefined;
  renderActionButtons: (mobile: boolean) => ReactNode;
  searchContext: SearchContextHeaderData | null | undefined;
  adminActions?: ReactNode;
  toolbar?: ReactNode;
  isOrg?: boolean;
  isAdmin?: boolean;
  banner?: ReactNode;
  footer?: ReactNode;
  urlByLocale: Record<string, string>;
};

export function ListingLayoutWrapper({
  listingType,
  actionType,
  orgType,
  isHiring,
  title,
  subtitle,
  details,
  sidebarButtons,
  sidebarTitle,
  sidebarImage,
  sidebarHeader,
  sidebarDetails,
  sidebarLinks,
  renderActionButtons,
  searchContext,
  adminActions,
  toolbar,
  isOrg,
  isAdmin,
  banner,
  footer,
  urlByLocale,
}: Props) {
  return (
    <ListingLayout
      adminActions={adminActions}
      toolbar={toolbar}
      isOrg={isOrg}
      isAdmin={isAdmin}
      listingType={listingType}
      urlByLocale={urlByLocale}
      header={
        <>
          {searchContext && (
            <PrintHidden>
              <Box mb={[4, 4, 4, '40px']}>
                <SearchContextHeader searchContext={searchContext} />
              </Box>
            </PrintHidden>
          )}
          <Box mb="24px">
            <ListingTypeLabel
              listingType={listingType}
              orgType={orgType}
              actionType={actionType}
            />
            {isHiring && (
              <Box mt="20px">
                <ActivelyHiringBadge />
              </Box>
            )}
          </Box>

          <H1
            styleAs="h2"
            color="mediumContentGrey"
            data-qa-id="listing-name"
            lineHeight={1}
            mb="24px"
          >
            {title}
          </H1>
          <Box lineHeight={1} mb={['12px', null, null, '24px']}>
            {subtitle}
          </Box>
          <Hide xs sm md>
            <div>{renderActionButtons(false)}</div>
          </Hide>
        </>
      }
      buttonBar={renderActionButtons(true)}
      main={
        <>
          {details}
          {searchContext && (
            <PrintHidden>
              <Box mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </>
      }
      banner={banner}
      footer={footer}
      sidebarTitle={
        sidebarTitle && (
          <PrintHidden>
            <Hide xs sm md>
              {sidebarTitle}
            </Hide>
          </PrintHidden>
        )
      }
      sidebarImage={
        sidebarImage && (
          <PrintHidden>
            <Hide xs sm md>
              <Box mb="16px">{sidebarImage}</Box>
            </Hide>
          </PrintHidden>
        )
      }
      sidebarButtons={sidebarButtons}
      sidebarLinks={
        (searchContext || sidebarLinks) && (
          <PrintHidden>
            <Hide xs sm md>
              {searchContext && (
                <Box pb={3}>
                  <RouterLink
                    to={searchContext.searchResultsPath}
                    data-qa-id="search-context-back-to-search-sidebar"
                  >
                    {getText('Back to search results')}
                  </RouterLink>
                </Box>
              )}
              {sidebarLinks}
            </Hide>
          </PrintHidden>
        )
      }
      sidebar={
        <>
          {sidebarHeader && <div>{sidebarHeader}</div>}
          {sidebarDetails}
        </>
      }
    />
  );
}
